import { useCallback, useState } from "react";

/**
 * Tracks an element's rendered size via ResizeObserver.
 *
 * Try-on needs this because the landmark-to-pixel mapping and the camera rig
 * are both derived from the container's actual pixel dimensions — a resize
 * that goes unnoticed puts the frames in the wrong place.
 */
export function useElementSize() {
  const [size, setSize] = useState(null);

  // Callback ref rather than useEffect: this fires as soon as the node is
  // attached, so the first measurement isn't a frame late.
  const ref = useCallback((node) => {
    if (!node) return;

    // By value, not by identity. Consumers hang expensive things off this
    // object — try-on hangs a coordinate mapping, a camera rig and its whole
    // smoothing history off it — and layout often settles over several
    // observer callbacks that land back on the same numbers. Handing out a
    // fresh object each time throws all of that away, which reads on screen
    // as the frames re-settling.
    const publish = (width, height) => {
      if (width <= 0 || height <= 0) return;
      setSize((current) =>
        current && current.width === width && current.height === height
          ? current
          : { width, height },
      );
    };

    const observer = new ResizeObserver(([entry]) => {
      publish(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(node);

    const rect = node.getBoundingClientRect();
    publish(rect.width, rect.height);

    return () => observer.disconnect();
  }, []);

  return [ref, size];
}
